'use client';

import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useMemo,
  useRef,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { useIsomorphicLayoutEffect } from '../../../hooks/use-isomorphic-layout-effect';
import { ChevronRight } from '../../../icons';
import { cn } from '../../../lib/utils';
import { Icon, type SvgIcon } from '../../icon/icon';
import { MENU_ICON_SIZE, menuItemVariants } from '../shared/menu-item-variants';
import { useMenuSize } from '../shared/menu-size-context';
import { useDrawerMenuContext } from './drawer-menu-context';
import { DrawerMenuPanel } from './drawer-menu';

type DrawerMenuSubContextValue = {
  id: string;
  /** Title set by the content, read by the trigger when it pushes the panel. */
  titleRef: MutableRefObject<ReactNode>;
};

const DrawerMenuSubContext = createContext<DrawerMenuSubContextValue | null>(null);

const useDrawerMenuSubContext = () => {
  const context = useContext(DrawerMenuSubContext);

  if (!context) {
    throw new Error('DrawerMenuSubTrigger and DrawerMenuSubContent belong to a DrawerMenuSub.');
  }

  return context;
};

export type DrawerMenuSubProps = {
  children?: ReactNode;
};

export const DrawerMenuSub = ({ children }: DrawerMenuSubProps) => {
  const id = useId();
  const titleRef = useRef<ReactNode>(undefined);
  const contextValue = useMemo(() => ({ id, titleRef }), [id]);

  return (
    <DrawerMenuSubContext.Provider value={contextValue}>{children}</DrawerMenuSubContext.Provider>
  );
};

export type DrawerMenuSubTriggerProps = Omit<
  ComponentPropsWithoutRef<'button'>,
  'type' | 'title' | 'children'
> & {
  children?: ReactNode;
  icon?: SvgIcon;
  /** Header title of the submenu. Defaults to the content's `title`, then to the row label. */
  title?: ReactNode;
};

export const DrawerMenuSubTrigger = forwardRef<HTMLButtonElement, DrawerMenuSubTriggerProps>(
  ({ className, children, icon, title, onClick, ...props }, ref) => {
    const { size } = useMenuSize();
    const { pushPanel } = useDrawerMenuContext();
    const { id, titleRef } = useDrawerMenuSubContext();

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      pushPanel({ id, title: titleRef.current ?? title ?? children });
    };

    return (
      <button
        ref={ref}
        type="button"
        data-slot="drawer-menu-sub-trigger"
        className={cn(menuItemVariants({ size }), 'text-left', className)}
        onClick={handleClick}
        {...props}>
        {icon ? <Icon icon={icon} size={MENU_ICON_SIZE[size]} /> : null}
        {children}
        <Icon icon={ChevronRight} size={MENU_ICON_SIZE[size]} className="ml-auto" />
      </button>
    );
  },
);
DrawerMenuSubTrigger.displayName = 'DrawerMenuSubTrigger';

export type DrawerMenuSubContentProps = {
  className?: string;
  /** Overrides the header title the trigger would use. */
  title?: ReactNode;
  children?: ReactNode;
};

export const DrawerMenuSubContent = ({ className, title, children }: DrawerMenuSubContentProps) => {
  const { id, titleRef } = useDrawerMenuSubContext();

  useIsomorphicLayoutEffect(() => {
    titleRef.current = title;
  });

  return (
    <DrawerMenuPanel id={id} className={className}>
      {children}
    </DrawerMenuPanel>
  );
};
